import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/** How long typing settles before the term is written to the URL. */
export const URL_SEARCH_DEBOUNCE_MS = 250;

/**
 * A search box's text, KEPT IN THE URL (owner, 2026-10-22: "search text kept in
 * the URL app-wide").
 *
 * The date, the year, the period beside a box all survived a reload and a
 * shared link; the text in the box did not, so a link to "the leave balances,
 * searching Rafiq" opened on everybody. The term is now `?q=` (or the `param`
 * a page with two boxes names for each).
 *
 * The box itself answers at once — `search` is local state — and the URL
 * catches up once typing settles, with `replace` so every keystroke is not a
 * history entry. A URL changed from outside (back, a link) wins over the box.
 * A blank box removes the parameter rather than writing `?q=`.
 */
export function useUrlSearchTerm(param = 'q'): [string, (value: string) => void] {
  const [sp, setSp] = useSearchParams();
  const fromUrl = sp.get(param) ?? '';
  const [search, setLocal] = useState(fromUrl);
  const written = useRef(fromUrl);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (fromUrl === written.current) return;
    written.current = fromUrl;
    setLocal(fromUrl);
  }, [fromUrl]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const setSearch = useCallback(
    (value: string) => {
      setLocal(value);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const next = value.trim() === '' ? '' : value;
        written.current = next;
        setSp(
          (prev) => {
            const p = new URLSearchParams(prev);
            if (next) p.set(param, next);
            else p.delete(param);
            return p;
          },
          { replace: true }
        );
      }, URL_SEARCH_DEBOUNCE_MS);
    },
    [param, setSp]
  );

  return [search, setSearch];
}
